/**
 * SopPanel Component (Feature #54)
 *
 * UI for managing Standard Operating Procedures:
 * - View discovered workflow patterns
 * - Generate SOPs from patterns
 * - View generated SOPs with markdown preview
 *
 * @module SopPanel
 */

'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Badge,
  Flex,
  Spinner,
  Card,
  Heading,
} from '@axon/ui';
import { useTranslations } from 'next-intl';
import {
  LuRefreshCw,
  LuBookOpen,
  LuFileText,
  LuChevronRight,
  LuArrowLeft,
  LuLightbulb,
  LuCheck,
  LuTriangleAlert,
} from 'react-icons/lu';

import { useSop, type WorkflowPattern, type GeneratedSOP } from '../hooks/useSop';

// ============================================================================
// Constants
// ============================================================================

/** Tab identifiers */
type TabId = 'patterns' | 'sops';

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * Pattern card for displaying a workflow pattern
 */
function PatternCard({
  pattern,
  onGenerate,
  isGenerating,
}: {
  pattern: WorkflowPattern;
  onGenerate: () => void;
  isGenerating: boolean;
}) {
  const t = useTranslations('chatbot');

  return (
    <Card.Root padding="3" marginBottom="2">
      <VStack align="stretch" gap="2">
        {/* Action sequence */}
        <HStack wrap="wrap" gap="1">
          {pattern.actionSequence.map((action, idx) => (
            <HStack key={idx} gap="1" align="center">
              <Badge size="sm" colorPalette="blue" variant="subtle">
                {action}
              </Badge>
              {idx < pattern.actionSequence.length - 1 && <LuChevronRight size={12} />}
            </HStack>
          ))}
        </HStack>

        {/* Metadata */}
        <HStack justify="space-between" align="center">
          <HStack gap="3">
            <Text fontSize="xs" color="text.muted">
              {t('sop.frequency')}: {pattern.frequency}
            </Text>
            {pattern.resourceTypes.length > 0 && (
              <Text fontSize="xs" color="text.muted">
                {pattern.resourceTypes.join(', ')}
              </Text>
            )}
          </HStack>
          <Button size="xs" variant="outline" onClick={onGenerate} disabled={isGenerating}>
            {isGenerating ? <Spinner size="xs" /> : t('sop.generate')}
          </Button>
        </HStack>
      </VStack>
    </Card.Root>
  );
}

/**
 * SOP card for displaying a generated SOP
 */
function SopCard({
  sop,
  onClick,
}: {
  sop: { id: string; title: string; sourcePath: string; createdAt: string };
  onClick: () => void;
}) {
  const createdDate = new Date(sop.createdAt).toLocaleDateString();

  return (
    <Card.Root
      padding="3"
      marginBottom="2"
      cursor="pointer"
      onClick={onClick}
      _hover={{ bg: 'bg.subtle' }}
    >
      <HStack justify="space-between" align="center">
        <VStack align="flex-start" gap="1">
          <HStack gap="2">
            <LuFileText size={16} />
            <Text fontWeight="medium" fontSize="sm">
              {sop.title}
            </Text>
          </HStack>
          <Text fontSize="xs" color="text.muted">
            {createdDate}
          </Text>
        </VStack>
        <LuChevronRight size={16} />
      </HStack>
    </Card.Root>
  );
}

/**
 * SOP detail view with markdown-like rendering
 */
function SopDetailView({ sop, onBack }: { sop: GeneratedSOP; onBack: () => void }) {
  const t = useTranslations('chatbot');

  return (
    <Box padding="4" overflow="auto" flex="1">
      <VStack align="stretch" gap="4">
        {/* Header with back button */}
        <HStack gap="2">
          <IconButton
            aria-label="Back"
            size="sm"
            variant="ghost"
            onClick={onBack}
            icon={<LuArrowLeft size={16} />}
          />
          <Heading size="sm">{sop.title}</Heading>
        </HStack>

        {/* Purpose */}
        <Box>
          <Text fontWeight="semibold" marginBottom="1">
            {t('sop.purpose')}
          </Text>
          <Text fontSize="sm" color="text.muted">
            {sop.purpose}
          </Text>
        </Box>

        {/* Prerequisites */}
        {sop.prerequisites.length > 0 && (
          <Box>
            <Text fontWeight="semibold" marginBottom="2">
              {t('sop.prerequisites')}
            </Text>
            <Box as="ul" paddingLeft="6" gap="1" style={{ listStyleType: 'disc' }}>
              {sop.prerequisites.map((prereq, idx) => (
                <Box as="li" key={idx} fontSize="sm" marginBottom="1">
                  {prereq}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Steps */}
        <Box>
          <Text fontWeight="semibold" marginBottom="2">
            {t('sop.steps')}
          </Text>
          <VStack align="stretch" gap="2">
            {sop.steps.map((step) => (
              <Card.Root key={step.number} padding="3" variant="outline">
                <HStack align="flex-start" gap="3">
                  <Badge colorPalette="blue" size="sm">
                    {step.number}
                  </Badge>
                  <VStack align="flex-start" gap="1" flex="1">
                    <Text fontSize="sm">{step.action}</Text>
                    {step.tip && (
                      <HStack gap="1" color="text.muted">
                        <LuLightbulb size={12} />
                        <Text fontSize="xs" fontStyle="italic">
                          {step.tip}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                </HStack>
              </Card.Root>
            ))}
          </VStack>
        </Box>

        {/* Best Practices */}
        {sop.bestPractices.length > 0 && (
          <Box>
            <Text fontWeight="semibold" marginBottom="2">
              {t('sop.bestPractices')}
            </Text>
            <Box as="ul" paddingLeft="6" gap="1" style={{ listStyleType: 'disc' }}>
              {sop.bestPractices.map((practice, idx) => (
                <Box as="li" key={idx} fontSize="sm" marginBottom="1">
                  <HStack gap="2" display="inline-flex">
                    <LuCheck size={12} color="green" />
                    <Text as="span">{practice}</Text>
                  </HStack>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </VStack>
    </Box>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * SOP Panel component
 *
 * Displays workflow patterns and generated SOPs with tab navigation.
 */
export function SopPanel() {
  const t = useTranslations('chatbot');
  const [activeTab, setActiveTab] = useState<TabId>('patterns');

  const {
    patterns,
    sops,
    activeSOP,
    isLoading,
    isGenerating,
    error,
    loadPatterns,
    generateSOP,
    loadSOPs,
    fetchSOP,
    setActiveSOP,
    clearError,
  } = useSop();

  // Load data on mount
  useEffect(() => {
    if (activeTab === 'patterns' && patterns.length === 0) {
      void loadPatterns();
    } else if (activeTab === 'sops' && sops.length === 0) {
      void loadSOPs();
    }
  }, [activeTab, patterns.length, sops.length, loadPatterns, loadSOPs]);

  // Handle tab change
  const handleTabChange = useCallback(
    (tab: TabId) => {
      setActiveTab(tab);
      setActiveSOP(null);
      clearError();
    },
    [setActiveSOP, clearError]
  );

  // Handle pattern generation
  const handleGenerate = useCallback(
    (pattern: WorkflowPattern) => {
      void generateSOP(pattern);
    },
    [generateSOP]
  );

  // Handle SOP click (fetch full details from backend)
  const handleSopClick = useCallback(
    (sopId: string) => {
      void fetchSOP(sopId);
    },
    [fetchSOP]
  );

  // Handle back from detail view
  const handleBackFromDetail = useCallback(() => {
    setActiveSOP(null);
  }, [setActiveSOP]);

  // Refresh current tab
  const handleRefresh = useCallback(() => {
    if (activeTab === 'patterns') {
      void loadPatterns();
    } else {
      void loadSOPs();
    }
  }, [activeTab, loadPatterns, loadSOPs]);

  return (
    <Box display="flex" flexDirection="column" height="100%" overflow="hidden">
      {/* Header */}
      <Flex
        paddingX="4"
        paddingY="3"
        align="center"
        justify="between"
        borderBottom="1px solid"
        borderColor="border"
      >
        <HStack gap="2">
          <LuBookOpen size={16} />
          <Text fontWeight="semibold">{t('sop.title')}</Text>
        </HStack>
        <IconButton
          aria-label="Refresh"
          size="sm"
          variant="ghost"
          onClick={handleRefresh}
          disabled={isLoading}
          icon={<LuRefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />}
        />
      </Flex>

      {/* Tab navigation */}
      {!activeSOP && (
        <HStack paddingX="4" paddingY="2" gap="2" borderBottom="1px solid" borderColor="border">
          <Button
            size="xs"
            variant={activeTab === 'patterns' ? 'primary' : 'ghost'}
            onClick={() => handleTabChange('patterns')}
          >
            {t('sop.patterns')}
          </Button>
          <Button
            size="xs"
            variant={activeTab === 'sops' ? 'primary' : 'ghost'}
            onClick={() => handleTabChange('sops')}
          >
            {t('sop.generated')}
          </Button>
        </HStack>
      )}

      {/* Error banner */}
      {error && (
        <Box paddingX="4" paddingY="2" bg="red.100" color="red.800" fontSize="sm">
          <HStack gap="2">
            <LuTriangleAlert size={14} />
            <Text>{error}</Text>
          </HStack>
        </Box>
      )}

      {/* Content */}
      <Box flex="1" overflow="auto">
        {/* SOP Detail View */}
        {activeSOP ? (
          <SopDetailView sop={activeSOP} onBack={handleBackFromDetail} />
        ) : (
          <>
            {/* Patterns Tab */}
            {activeTab === 'patterns' && (
              <Box padding="3">
                {isLoading && patterns.length === 0 ? (
                  <Flex justify="center" padding="8">
                    <Spinner size="md" />
                  </Flex>
                ) : patterns.length === 0 ? (
                  <Text color="text.muted" textAlign="center" padding="8">
                    {t('sop.noPatterns')}
                  </Text>
                ) : (
                  <VStack align="stretch" gap="2">
                    <Text fontSize="xs" color="text.muted" marginBottom="2">
                      {t('sop.patternsHint')}
                    </Text>
                    {patterns.map((pattern, idx) => (
                      <PatternCard
                        key={pattern.patternHash || idx}
                        pattern={pattern}
                        onGenerate={() => handleGenerate(pattern)}
                        isGenerating={isGenerating}
                      />
                    ))}
                  </VStack>
                )}
              </Box>
            )}

            {/* SOPs Tab */}
            {activeTab === 'sops' && (
              <Box padding="3">
                {isLoading && sops.length === 0 ? (
                  <Flex justify="center" padding="8">
                    <Spinner size="md" />
                  </Flex>
                ) : sops.length === 0 ? (
                  <Text color="text.muted" textAlign="center" padding="8">
                    {t('sop.noSops')}
                  </Text>
                ) : (
                  <VStack align="stretch" gap="2">
                    {sops.map((sop) => (
                      <SopCard key={sop.id} sop={sop} onClick={() => handleSopClick(sop.id)} />
                    ))}
                  </VStack>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export default SopPanel;
